import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  MessageSquare,
  MoreVertical,
  Trash2,
  Pencil,
  Box,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { HistoryConversation } from '@/types/misc';
import { firestore } from '@/lib/firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { useOpenSCAD } from '@/hooks/useOpenSCAD';
import { Canvas } from '@react-three/fiber';
import {
  OrbitControls,
  Stage,
  Environment,
  PerspectiveCamera,
} from '@react-three/drei';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { BufferGeometry } from 'three';

function hasRenderableOpenSCAD(code: string): boolean {
  return !/module\s+assembly\s*\(\)\s*\{\s*\/\/ no parts defined\s*\}/s.test(
    code,
  );
}
import { downloadConversationAssetBlob } from '@/lib/conversationAssets';

interface VisualCardProps {
  conversation: HistoryConversation;
  onDelete: (conversationId: string) => void;
  onRename: (conversationId: string, newTitle: string) => void;
}

function ThreePreview({ geometry }: { geometry: BufferGeometry }) {
  return (
    <Canvas
      className="h-full w-full"
      frameloop="demand"
      gl={{ powerPreference: 'low-power', antialias: true }}
    >
      <color attach="background" args={['#EFE3CA']} />
      <PerspectiveCamera
        makeDefault
        position={[-100, 100, 100]}
        fov={45}
        near={0.1}
        far={1000}
        zoom={0.4}
      />
      <Stage environment={null} intensity={0.6} position={[0, 0, 0]}>
        <Environment files={`${import.meta.env.BASE_URL}city.hdr`} />
        <ambientLight intensity={0.8} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />
        <directionalLight position={[-5, 5, 5]} intensity={0.2} />
        <mesh
          geometry={geometry}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, 0, 0]}
        >
          <meshStandardMaterial
            color="#56B6C6"
            metalness={0.15}
            roughness={0.6}
            envMapIntensity={0.5}
          />
        </mesh>
      </Stage>
      <OrbitControls
        makeDefault
        enableDamping={false}
        enableZoom={false}
      />
    </Canvas>
  );
}

export function VisualCard({
  conversation,
  onDelete,
  onRename,
}: VisualCardProps) {
  const { t } = useTranslation('common');
  const [artifactCode, setArtifactCode] = useState<string | null>(null);
  const [artifactStoragePath, setArtifactStoragePath] = useState<string | null>(
    null,
  );
  const [geometry, setGeometry] = useState<BufferGeometry | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const { compileScad, isCompiling, output, isError } = useOpenSCAD();

  useEffect(() => {
    const currentCard = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin: '100px' },
    );

    if (currentCard) {
      observer.observe(currentCard);
    }

    return () => {
      if (currentCard) {
        observer.unobserve(currentCard);
      }
    };
  }, []);

  useEffect(() => {
    const fetchLastArtifact = async () => {
      try {
        const messagesCol = collection(
          firestore,
          'conversations',
          conversation.id,
          'messages',
        );
        const q = query(
          messagesCol,
          where('role', '==', 'assistant'),
          orderBy('created_at', 'desc'),
          limit(50),
        );
        const snap = await getDocs(q);
        const messages = snap.docs.map((d) => d.data());

        const messageWithArtifact = messages?.find(
          (msg) =>
            msg.content &&
            typeof msg.content === 'object' &&
            'artifact' in msg.content &&
            msg.content.artifact,
        );

        if (
          messageWithArtifact &&
          messageWithArtifact.content &&
          typeof messageWithArtifact.content === 'object' &&
          'artifact' in messageWithArtifact.content
        ) {
          const artifact = messageWithArtifact.content.artifact;
          if (
            artifact &&
            typeof artifact === 'object' &&
            'code' in artifact &&
            typeof artifact.code === 'string'
          ) {
            setArtifactCode(artifact.code);
            // Find the preview-role STL file in the new ArtifactFile[] shape.
            const files =
              'files' in artifact && Array.isArray(artifact.files)
                ? (artifact.files as Array<{
                    role?: string;
                    format?: string;
                    storagePath?: string;
                  }>)
                : [];
            const previewFile = files.find(
              (f) =>
                f &&
                typeof f === 'object' &&
                f.role === 'preview' &&
                f.format === 'stl' &&
                typeof f.storagePath === 'string',
            );
            setArtifactStoragePath(
              previewFile?.storagePath ? previewFile.storagePath : null,
            );
          }
        }
      } catch (error) {
        console.error('Error fetching artifact:', error);
      }
    };

    fetchLastArtifact();
  }, [conversation.id]);

  useEffect(() => {
    if (!artifactStoragePath && artifactCode && hasRenderableOpenSCAD(artifactCode)) {
      compileScad(artifactCode);
    }
  }, [artifactCode, artifactStoragePath, compileScad]);

  useEffect(() => {
    let isMounted = true;

    const loadStoredArtifact = async () => {
      if (!artifactStoragePath) {
        return;
      }

      try {
        const blob = await downloadConversationAssetBlob(
          conversation.id,
          artifactStoragePath,
        );
        const buffer = await blob.arrayBuffer();
        if (!isMounted) {
          return;
        }

        const loader = new STLLoader();
        const geom = loader.parse(buffer);
        geom.center();
        geom.computeVertexNormals();
        setGeometry(geom);
      } catch (error) {
        console.error('Error loading stored artifact:', error);
      }
    };

    loadStoredArtifact();

    return () => {
      isMounted = false;
    };
  }, [artifactStoragePath]);

  useEffect(() => {
    let isMounted = true;

    if (!artifactStoragePath && output && output instanceof Blob) {
      output
        .arrayBuffer()
        .then((buffer) => {
          if (isMounted) {
            const loader = new STLLoader();
            const geom = loader.parse(buffer);
            geom.center();
            geom.computeVertexNormals();
            setGeometry(geom);
          }
        })
        .catch((error) => {
          console.error('Error loading STL:', error);
        });
    } else {
      setGeometry(null);
    }

    return () => {
      isMounted = false;
    };
  }, [output, artifactStoragePath]);

  return (
    <div
      ref={cardRef}
      className="group relative overflow-hidden rounded-2xl border-2 border-brand-indigo/15 bg-k-card transition-colors duration-200 hover:border-brand-indigo"
    >
      <Link to={`/editor/${conversation.id}`}>
        <div className="relative aspect-square w-full overflow-hidden bg-brand-cream">
          {!isVisible ? (
            <div className="flex h-full w-full items-center justify-center">
              <Box className="h-16 w-16 text-brand-indigo/30" strokeWidth={1.5} />
            </div>
          ) : isCompiling ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-brand-indigo" />
                <span className="text-xs font-bold uppercase tracking-wider text-brand-indigo/70">
                  {t('history.compiling')}
                </span>
              </div>
            </div>
          ) : isError || !geometry ? (
            <div className="flex h-full w-full items-center justify-center">
              <Box className="h-16 w-16 text-brand-indigo/40" strokeWidth={1.5} />
            </div>
          ) : (
            <ThreePreview geometry={geometry} />
          )}
        </div>

        <div className="border-t-2 border-brand-indigo/15 p-4">
          <h3 className="mb-2 line-clamp-2 font-display text-base font-bold text-brand-indigo">
            {conversation.title}
          </h3>
          <div className="flex items-center gap-3 text-xs font-medium text-brand-indigo/70">
            <span className="flex items-center">
              <Clock className="mr-1 h-3 w-3" />
              {formatDistanceToNow(new Date(conversation.updated_at), {
                addSuffix: true,
                locale: ja,
              })}
            </span>
            <span className="flex items-center">
              <MessageSquare className="mr-1 h-3 w-3" />
              {t('chat.messagesCount', { count: conversation.message_count })}
            </span>
          </div>
        </div>
      </Link>

      <div className="absolute right-2 top-2">
        <AlertDialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 rounded-full bg-k-card/80 p-0 backdrop-blur-sm transition-colors duration-200 hover:bg-brand-indigo/10"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4 text-brand-indigo" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-2 border-brand-indigo bg-k-card">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onRename(conversation.id, conversation.title);
                }}
                className="text-brand-indigo hover:cursor-pointer hover:bg-brand-teal-soft focus:bg-brand-teal-soft"
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t('history.rename')}
              </DropdownMenuItem>
              <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem className="font-semibold text-brand-indigo hover:cursor-pointer hover:bg-brand-indigo hover:text-brand-cream focus:bg-brand-indigo focus:text-brand-cream">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('dialog.delete')}
                </DropdownMenuItem>
              </AlertDialogTrigger>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialogContent className="border-2 border-brand-indigo bg-k-card">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-brand-indigo">
                {t('history.deleteProjectTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('history.deleteProjectDesc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
                {t('dialog.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(conversation.id);
                }}
                className="bg-brand-indigo text-brand-cream hover:bg-brand-indigo-hover"
              >
                {t('dialog.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
